import React, { useState, useEffect } from 'react';
import { Download, X, ChevronDown, ChevronRight, ArrowUp, ArrowDown, Search } from 'lucide-react';
import  GridComponent from './UpdatableGrid'
import { API_URL } from './constants';
import {loadDatasetContent, handleDownload} from './APICalls';
import { ToastContainer } from 'react-toastify';
import CompactPopupForm from './UserDetailsForm'
import DownloadDropdown from './DownloadDropdown';

const MyOrdersPage = ({user}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFeedback, setShowFeedback] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [detailData, setDetailData] = useState({});
  const [columnDefs, setColumnDefs] = useState({});
  const [isFeedbackSubmitted, setIsFeedbackSubmitted] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
      try {
      const response = await fetch(`${API_URL}/api/orders`,{
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${user.accessToken}`
          },
          body: JSON.stringify({"user_id": user.uid})
      }); // Replace with your API endpoint
        if (!response.ok) {
          throw new Error('Failed to fetch entries');
        }
        const data = await response.json();
        console.log(data);
        setData(data["orders"]);
        setLoading(false);
      } catch (err) {
        console.log(err.message);
      }
  };

  const fetchDetailData = async (rowId, filename) => {
    var results = await loadDatasetContent(filename, user.accessToken);
    // alert('Fetched results:', results);

    if(results === null || results === "") {
      return;
    }

    // Store column defs per row
    setColumnDefs(prev => ({
      ...prev,
      [rowId]: results[0]
    }));
    
    
    setDetailData(prev => ({
      ...prev,
      [rowId]: results[1]
    }));
};

  const toggleRow = async (rowId, filename) => {
  const isExpanding = !expandedRows[rowId];
    
    if (isExpanding && !detailData[rowId]) {
      await fetchDetailData(rowId, filename);
    }
    
    setExpandedRows(prev => ({
      ...prev,
      [rowId]: isExpanding
    }));
};

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-blue-900 mb-2">Data Management</h1>
          <p className="text-gray-600">Download and manage your datasets</p>
        </div>

        <div className="bg-white rounded-lg shadow-lg overflow-y-visible">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">Loading data...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full relative">
                <thead className="bg-blue-600 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold w-12"></th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Prompt</th>
                    {/* <th className="px-6 py-4 text-left text-sm font-semibold">Purpose</th> */}
                    <th className="px-6 py-4 text-left text-sm font-semibold">Number</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">DateTime</th>
                    <th className="px-6 py-4 text-right text-sm font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data.map((row, index) => (
                    <React.Fragment key={index}>
                      <tr 
                        onClick={row.order_filename ? async () => await toggleRow(index, row.order_filename) : undefined}
                        className={`cursor-pointer hover:bg-blue-50 transition-colors ${
                          index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                        } ${expandedRows[index] ? 'bg-blue-100' : ''}`}
                      >
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {expandedRows[index] ? 
                            <ChevronDown size={18} className="text-blue-600" /> : 
                            <ChevronRight size={18} className="text-gray-400" />
                          }
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {row.prompt.length > 100 ? `${row.prompt.substring(0, 100)}...` : row.prompt}
                        </td>
                        {/* <td className="px-6 py-4 text-sm font-medium text-gray-900">{row.purpose}</td> */}
                        <td className="px-6 py-4 text-sm text-gray-600">{row.number}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {row.timestamp}
                        </td>
                        {/* <td className="px-6 py-4">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {row.status}
                          </span>
                        </td> */}
                        {row.order_filename && (<td className="px-6 py-4 text-right relative z-10">
                          <DownloadDropdown
                            onDownload={(format) => {
                             if(isFeedbackSubmitted){
                              handleDownload(format, columnDefs[index], detailData[index]);
                             }
                             else{
                              setShowFeedback(true);
                             }
                            }}
                          />
                        </td> )}
                      </tr>
                      {expandedRows[index] && (
                        <tr>
                          <td colSpan="7" className="px-6 py-4 bg-gray-50 relative z-0">
                            <div style={{ width: '100%' }}>
                              {detailData[index] ? (
                                <GridComponent
                                  rowData={detailData[index]}
                                  colDefs={columnDefs[index]}
                                />
                              ) : (
                                <div className="flex items-center justify-center py-12">
                                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-4 text-sm text-gray-600">
          Total Records: {data.length}
        </div>
      </div>

      {showFeedback && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <CompactPopupForm
            open={true}
            onClose={() => setShowFeedback(false)}
            setIsFeedbackSubmitted={setIsFeedbackSubmitted}
          />
        </div>
      )}
      <ToastContainer position="top-center" autoClose={3000} />
    </div>
  );
};

export default MyOrdersPage;